/**
 * Public configuration (URLs, environment names). Do NOT put secrets here — the web bundle is fully exposed.
 *
 * Priority order:
 * 1. `EXPO_PUBLIC_*` (embedded directly in the bundle)
 * 2. `react-native-config` (native / dev client, reads from .env)
 * 3. `expo.extra` in app.config.js — mapped from `.env` (e.g., FIREBASE_CLOUD_FUNCTION_URL, …) so web can share the same source as native
 */
import Constants from 'expo-constants';
import Config from 'react-native-config';

type ExtraKey =
  | 'firebaseCloudFunctionUrl'
  | 'pictureUrl'
  | 'pinUrl'
  | 'environment'
  | 'sentryDsn'
  | 'usKidGolfApiUrl'
  | 'bookingEngineApiUrl'
  | 'ssoIssuerUrl'
  | 'iosAppStoreUrl'
  | 'androidPlayStoreUrl'
  | 'googleMapsApiKey';

function readConfig(key: keyof typeof Config): string | undefined {
  const raw = Config[key];
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim();
  return v === '' ? undefined : v;
}

function readLegacy(key: string): string | undefined {
  const raw = (Config as Record<string, string | undefined>)[key];
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim();
  return v === '' ? undefined : v;
}

function readExpoExtra(key: ExtraKey): string | undefined {
  const extra = Constants.expoConfig?.extra as Record<string, string | undefined> | undefined;
  const raw = extra?.[key];
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim();
  return v === '' ? undefined : v;
}

export const envPublic = {
  get environment(): string {
    // `readExpoExtra` fallback is required on web: react-native-config is native-only,
    // so without it `environment` is empty on web and Sentry (env.public consumer)
    // falls back to 'production' on a live build — reporting to the wrong environment
    // even though the API URLs correctly resolve to dev via their own extra fallback.
    return readConfig('ENVIRONMENT') ?? readLegacy('ENV') ?? readExpoExtra('environment') ?? '';
  },

  get firebaseCloudFunctionUrl(): string {
    return (
      readConfig('FIREBASE_CLOUD_FUNCTION_URL') ?? readExpoExtra('firebaseCloudFunctionUrl') ?? ''
    );
  },

  get usKidGolfApiUrl(): string {
    return readConfig('US_KID_GOLF_API_URL') ?? readExpoExtra('usKidGolfApiUrl') ?? '';
  },

  /** Booking-engine backend — public coach-directory search for "Find a nearby coach".
   *  Uses `readLegacy` because the key isn't in react-native-config's generated `NativeConfig`. */
  get bookingEngineApiUrl(): string {
    return readLegacy('BOOKING_ENGINE_API_URL') ?? readExpoExtra('bookingEngineApiUrl') ?? '';
  },

  /** OIDC IdP origin used only by the SSO interaction client. Separate from the API URL
   *  because the IdP must live on a subdomain of the web's domain (same-site cookie). */
  get ssoIssuerUrl(): string {
    return readConfig('SSO_ISSUER_URL') ?? readExpoExtra('ssoIssuerUrl') ?? '';
  },

  get pictureUrl(): string {
    return readConfig('PICTURE_URL') ?? readExpoExtra('pictureUrl') ?? '';
  },

  get pinUrl(): string {
    return readConfig('PIN_URL') ?? readExpoExtra('pinUrl') ?? '';
  },

  get sentryDsn(): string {
    return readConfig('SENTRY_DSN') ?? readExpoExtra('sentryDsn') ?? '';
  },

  get iosAppStoreUrl(): string {
    return readLegacy('IOS_APP_STORE_URL') ?? readExpoExtra('iosAppStoreUrl') ?? '';
  },

  get androidPlayStoreUrl(): string {
    return readLegacy('ANDROID_PLAY_STORE_URL') ?? readExpoExtra('androidPlayStoreUrl') ?? '';
  },

  /** Maps JavaScript API key for the web-only interactive map. Native reads
   *  GOOGLE_MAP_API_KEY directly via react-native-config; web gets it from expo.extra. */
  get googleMapsApiKey(): string {
    return readLegacy('GOOGLE_MAP_API_KEY') ?? readExpoExtra('googleMapsApiKey') ?? '';
  },
} as const;

export function warnIfMissingCloudFunctionUrlInDev(): void {
  if (__DEV__ && !envPublic.firebaseCloudFunctionUrl) {
    console.warn(
      '[envPublic] Missing Cloud Functions URL. Add FIREBASE_CLOUD_FUNCTION_URL to .env (and restart Metro), or set it in extra within app.config.js',
    );
  }
}
